import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { AdminGraphQLBaseService } from "src/app/module_services/admin-graphql-base.service";
import { environment } from "src/environments/environment";

export interface AdminAuthProfile {
  id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  superuser?: boolean;
  photo?: string;
}

export interface AdminAuthSession {
  token?: string;
  permissions?: string[];
  profile?: AdminAuthProfile;
  org?: { id?: string; name?: string };
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private static readonly STORAGE_KEY = "currentUser";
  private currentUserSubject: BehaviorSubject<AdminAuthSession | null>;
  public currentUser: Observable<AdminAuthSession | null>;

  constructor(
    private _adminBase: AdminGraphQLBaseService,
    private _router: Router
  ) {
    this.currentUserSubject = new BehaviorSubject<AdminAuthSession | null>(
      this.readStoredSession()
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): AdminAuthSession | null {
    return this.currentUserSubject.value;
  }

  public set setCurrentUserValue(user: AdminAuthSession | null) {
    if (user != null) {
      localStorage.setItem(AuthService.STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(AuthService.STORAGE_KEY);
    }
    this.currentUserSubject.next(user);
  }

  getDisplayName(session: AdminAuthSession | null = this.currentUserValue): string {
    const profile = session?.profile;
    if (!profile) {
      return "";
    }
    return [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim();
  }

  getAvatarInitial(session: AdminAuthSession | null = this.currentUserValue): string {
    const firstName = session?.profile?.first_name?.trim();
    if (!firstName) {
      return "";
    }
    return firstName.charAt(0).toUpperCase();
  }

  getProfileEmail(session: AdminAuthSession | null = this.currentUserValue): string {
    return session?.profile?.email?.trim() ?? "";
  }

  getRoleLabel(session: AdminAuthSession | null = this.currentUserValue): string {
    if (!session?.profile) {
      return "";
    }
    return session.profile.superuser ? "Super User" : "User";
  }

  private readStoredSession(): AdminAuthSession | null {
    const raw = localStorage.getItem(AuthService.STORAGE_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as AdminAuthSession;
    } catch {
      localStorage.removeItem(AuthService.STORAGE_KEY);
      return null;
    }
  }

  login(inputs: { email: string; password: string }) {
    return this._adminBase.doMutate(`
      mutation login ($input: LoginInput!) {
        login(input: $input) {
          token
          permissions
          profile {
            id
            first_name
            last_name
            email
            superuser
          }
        }
      }
    `, { input: inputs }).pipe(
      map((user: any) => {
        this.setCurrentUserValue = user.data.login;
        return user;
      })
    );
  }

  logout() {
    this.setCurrentUserValue = null;
    this._router.navigate(["/login"]);
  }

  getPermittedMenu(items: any[], hideInSideNav: boolean = false) {
    const filteredItems = items.filter((item: any) => {
      if (item.submenu?.length > 0) {
        item.submenu = this.getPermittedMenu(item.submenu);
      }
      const isProdEnv = environment.production;
      if (isProdEnv && !item.isDisplayInProd) return false;
      if (hideInSideNav && item.hideInSideNav) return false;

      const permissionName = item.permissionName;
      let isDisplayMenu = false;
      if (!item.isPrivileged) {
        isDisplayMenu = true;
      } else if (this.currentUserValue?.profile?.superuser) {
        isDisplayMenu = true;
      } else {
        const permissions = this.currentUserValue?.permissions ?? [];
        if (permissions.length > 0 && permissions.includes(permissionName)) {
          isDisplayMenu = true;
        }
      }
      if (isDisplayMenu) return item;
      return false;
    });

    return filteredItems;
  }
}
